import gql from "graphql-tag"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { useCurrentUser } from "@modules/auth"
import {
  useToggleIncrementalBuildFlagMutation,
  ToggleIncrementalBuildFlagMutation,
} from "../queries.generated"
import { MutationUpdaterFn } from "apollo-client"
import { DataProxy } from "apollo-cache"
import { Site } from "@modules/graphql/types"

export default function useToggleIncrementalBuilds(siteId: string) {
  const { currentUser } = useCurrentUser()
  const currentUserId = currentUser && currentUser.id

  const [
    toggleIncrementalBuildFlagMutation,
    { loading },
  ] = useToggleIncrementalBuildFlagMutation()

  const [setError, errorAlert] = useTriggerErrorAlert()

  const toggleIncrementalBuilds = () => {
    setError(null)
    return toggleIncrementalBuildFlagMutation({
      variables: {
        siteId,
        userId: currentUserId,
      },
      update: updateIncrementalBuildsEnabledCache(siteId) as MutationUpdaterFn<
        ToggleIncrementalBuildFlagMutation
      >,
      optimisticResponse: {
        toggleIncrementalBuildFlag: {
          success: true,
          message: null,
          __typename: `MutationResult`,
        },
      },
    }).catch(err => setError(err))
  }

  return [toggleIncrementalBuilds, { loading, errorAlert }]
}

function updateIncrementalBuildsEnabledCache(siteId: string) {
  return (
    proxy: DataProxy,
    { data }: { data: ToggleIncrementalBuildFlagMutation }
  ) => {
    if (!data?.toggleIncrementalBuildFlag?.success) {
      return
    }
    const fragmentInfo = {
      id: `Site:${siteId}`,
      fragment: gql`
        fragment siteDetailsIncrementalBuildsFrag on Site {
          id
          incrementalBuildsEnabled
        }
      `,
    }

    const site: Site | null = proxy.readFragment(fragmentInfo)

    if (site) {
      site.incrementalBuildsEnabled = !site.incrementalBuildsEnabled
    }

    proxy.writeFragment({
      ...fragmentInfo,
      data: site,
    })
  }
}
